import {
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  OnInit
} from '@angular/core';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { VexConfigService } from '@vex/config/vex-config.service';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
import { NavigationService } from '../../../core/navigation/navigation.service';
import { environment } from '../../../../environments/environment';
import { VexPopoverService } from '@vex/components/vex-popover/vex-popover.service';
import { MegaMenuComponent } from './mega-menu/mega-menu.component';
import { Observable, of } from 'rxjs';
import { NavigationComponent } from '../navigation/navigation.component';
import { ToolbarUserComponent } from './toolbar-user/toolbar-user.component';
import { AlertasLeadsComponent } from './alertas-leads/alertas-leads.component';
import { AlertasTareasComponent } from './alertas-tareas/alertas-tareas.component';
import { AlertasPostulacionesComponent } from './alertas-postulaciones/alertas-postulaciones.component';
import { NavigationItemComponent } from '../navigation/navigation-item/navigation-item.component';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NavigationItem } from '../../../core/navigation/navigation-item.interface';
import { checkRouterChildsData } from '@vex/utils/check-router-childs-data';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../core/auth/auth.service';

@Component({
  selector: 'vex-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    NgIf,
    RouterLink,
    MatMenuModule,
    NgClass,
    NgFor,
    NavigationItemComponent,
    AlertasLeadsComponent,
    AlertasTareasComponent,
    AlertasPostulacionesComponent,
    ToolbarUserComponent,
    NavigationComponent,
    AsyncPipe
  ]
})
export class ToolbarComponent implements OnInit {
  @HostBinding('class.shadow-b')
  showShadow: boolean = false;

  /** Advertencia de ambiente, vacía en producción.
   *
   *  DEV comparte la llave de secretos con producción —no hay ambiente de
   *  pruebas de redes sociales—, así que desde ahí se publica en las cuentas
   *  reales del cliente. Como eso no se puede evitar, al menos se avisa, y se
   *  avisa donde siempre se ve: arriba de todo, en cada pantalla. */
  readonly avisoAmbiente = environment.avisoAmbiente;

  navigationItems$: Observable<NavigationItem[]> =
    this.navigationService.items$;

  isHorizontalLayout$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.layout === 'horizontal')
  );
  isVerticalLayout$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.layout === 'vertical')
  );
  isNavbarInToolbar$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.navbar.position === 'in-toolbar')
  );
  isNavbarBelowToolbar$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.navbar.position === 'below-toolbar')
  );
  userVisible$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.toolbar.user.visible)
  );
  title$: Observable<string> = this.configService.select(
    (config) => config.sidenav.title
  );

  isDesktop$: Observable<boolean> = this.layoutService.isDesktop$;
  megaMenuOpen$: Observable<boolean> = of(false);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly auth = inject(AuthService);

  /** La campana de OBRAS sólo tiene sentido —y sólo está permitida— para quien
   * trabaja en Proyectos. El backend lo exige desde el guard de
   * `/tasks/mis-alertas`; acá se evita el 403 en cada ciclo de sondeo. */
  readonly tieneProyectos = this.auth.tieneModulo('proyectos');

  /** Cada campana se muestra sólo a quien trabaja en esa área.
   *
   * Andrés lo reportó así: «está recibiendo notificaciones correspondientes a
   * otras áreas y procesos que no son de su competencia» (acta 03/09, punto 3).
   * Un Community Manager veía la campana de postulaciones de Selección, que
   * además le devolvía 403 en cada ciclo de sondeo —el endpoint exige
   * `gestion_humana`— así que era una campana que nunca podía mostrarle nada y
   * encima disparaba el error global del interceptor cada 60 segundos.
   *
   * El backend ya lo exige en los tres endpoints; esto evita el 403 y el ruido
   * visual de una campana que no le sirve a quien la ve. */
  readonly tieneGestionHumana = this.auth.tieneModulo('gestion_humana');
  readonly tieneLeads = this.auth.tieneModulo('leads');

  constructor(
    private readonly layoutService: VexLayoutService,
    private readonly configService: VexConfigService,
    private readonly navigationService: NavigationService,
    private readonly popoverService: VexPopoverService,
    private readonly router: Router
  ) {}

  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.showShadow = checkRouterChildsData(
          this.router.routerState.root.snapshot,
          (data) => data.toolbarShadowEnabled ?? false
        );
      });
  }


  openSidenav(): void {
    this.layoutService.openSidenav();
  }

  openMegaMenu(origin: ElementRef | HTMLElement): void {
    this.megaMenuOpen$ = of(
      this.popoverService.open({
        content: MegaMenuComponent,
        origin,
        offsetY: 12,
        position: [
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top'
          }
        ]
      })
    ).pipe(
      switchMap((popoverRef) => popoverRef.afterClosed$.pipe(map(() => false))),
      startWith(true)
    );
  }

  openSearch(): void {
    this.layoutService.openSearch();
  }
}
